import { Component, inject, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import Order from '@client/Order'
import { switchMap } from 'rxjs/operators'
import { OrdersService } from '@client/Services/OrdersService'
import HttpError from '@ui/HttpError'
import { MatStep, MatStepLabel, MatStepper } from '@angular/material/stepper'
import { NgStyle } from '@angular/common'
import { ToastrService } from 'ngx-toastr'
import { CardComponent } from '@ui/Components/Card/Card.Component'
import { CardTitleComponent } from '@ui/Components/Card/CardTitle.Component'
import { SystemConfigService } from '@client/Services/SystemConfigService'
import { CardContentComponent } from '@ui/Components/Card/CardContent.Component'
import { BaseCurrencyComponent } from '@ui/Components/Currency/BaseCurrency.Component'
import { RaisedButtonComponent } from '@ui/Components/Form/Button/RaisedButton.Component'
import {
	InputValue,
	TextInputComponent,
} from '@ui/Components/Form/TextInput/TextInput.Component'

@Component({
	selector: 'setup-manual-order-payment-plan',
	templateUrl: './SetupOrderPaymentPlan.Component.html',
	imports: [
		MatStepper,
		NgStyle,
		MatStep,
		MatStepLabel,
		CardComponent,
		CardTitleComponent,
		CardContentComponent,
		BaseCurrencyComponent,
		RaisedButtonComponent,
		TextInputComponent,
	],
})
export class SetupOrderPaymentPlanComponent implements OnInit {
	@ViewChild('stepper') private readonly stepper: MatStepper

	protected step: number = 0

	protected order: Order

	protected initialPaymentAmount: string

	protected remaining: number = 0

	protected readonly systemConfigService = inject(SystemConfigService)

	private readonly route: ActivatedRoute

	private readonly ordersService: OrdersService

	private readonly toastrService: ToastrService

	private readonly router: Router

	constructor() {
		this.route = inject(ActivatedRoute)
		this.ordersService = inject(OrdersService)
		this.toastrService = inject(ToastrService)
		this.systemConfigService = inject(SystemConfigService)
		this.router = inject(Router)
	}

	public ngOnInit() {
		this.route.paramMap
			.pipe(
				switchMap(async (params: ParamMap) => {
					try {
						const step = params.get('step')

						if (step === '1') {
							this.step = 0
						} else if (step === '2') {
							this.step = 1
						} else if (step === 'failed') {
							this.step = 0

							this.toastrService.error(
								'Failed to handle your request. Please try again.',
								'Error',
							)
						}

						return this.ordersService.findById(parseInt(params.get('id'), 10))
					} catch (error) {
						if (!(error instanceof HttpError)) {
							throw error
						}
					}
				}),
			)
			.subscribe((order: Order) => {
				if (order) {
					this.order = order
					this.initialPaymentAmount = (
						this.systemConfigService.config.payment.initial_payment_amount /
						10 ** this.order.currency_precision
					).toString(10)
					this.remaining =
						this.order.total -
						this.systemConfigService.config.payment.initial_payment_amount
				}
			})
	}

	protected readonly proceedToNextStep = () => {
		const step = this.stepper.selectedIndex

		for (let i = 0; i <= step; i++) {
			this.stepper.steps.get(i).completed = true
			this.stepper.steps.get(i).interacted = true
		}

		this.step = step + 1
		this.stepper.next()
	}

	protected readonly viewOrders = () => {
		this.router.navigateByUrl('/my-account/orders')
	}

	protected readonly handlePaymentAmounts = (amount: InputValue) => {
		const amountAsInt =
			parseFloat(amount as string) * 10 ** this.order.currency_precision

		this.remaining = isNaN(amountAsInt) ? 0 : this.order.total - amountAsInt
	}
}
